import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, X } from 'lucide-react';
import { Product } from '../types';

interface ProductModalProps {
  product: Product;
  onClose: () => void;
  onQuoteClick: () => void;
}

const ProductModal: React.FC<ProductModalProps> = ({ product, onClose, onQuoteClick }) => {
  const { t, i18n } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // product.image (the admin-chosen cover) must come first — images[] can
  // still contain a stale copy of a PREVIOUS cover (replacing the cover in
  // the admin form doesn't retroactively remove it from the gallery list),
  // and deduping keeps that leftover from showing up twice.
  const images = [product.image, ...(product.images ?? [])].filter((v, i, arr) => !!v && arr.indexOf(v) === i);
  const description = product.description[i18n.language.startsWith('en') ? 'en' : 'fr'];
  const categoryLabel = t(`products.${product.category}.title`, product.category);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end justify-center bg-coffee-200/55 p-0 backdrop-blur-md sm:items-center sm:p-5"
        role="presentation"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="w-full sm:w-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="relative flex max-h-[min(94dvh,860px)] w-full flex-col overflow-hidden rounded-t-[1.75rem] border border-white/60 bg-cream-50 shadow-2xl shadow-coffee-200/25 sm:max-h-[min(90dvh,860px)] sm:max-w-2xl sm:rounded-[1.75rem] lg:max-w-4xl lg:flex-row"
            role="dialog"
            aria-modal="true"
            aria-labelledby="product-modal-title"
          >
            {/* Close button — floats over the panel, always reachable */}
            <button
              onClick={onClose}
              aria-label={t('products.modal.close', 'Fermer')}
              className="absolute right-4 top-4 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-cream-50/80 text-coffee-200 shadow-soft backdrop-blur-sm transition-colors hover:bg-cream-50"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>

            {/* Gallery — top bar on mobile/tablet, left column on desktop */}
            <div className="relative h-64 flex-shrink-0 overflow-hidden bg-gradient-to-br from-cream-100 to-cream-200 sm:h-80 lg:h-auto lg:w-[45%] lg:self-stretch">
              {images.map((image, index) => (
                <motion.img
                  key={image + index}
                  src={image}
                  alt={`${product.name} ${index + 1}`}
                  className="absolute inset-0 h-full w-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: currentImageIndex === index ? 1 : 0 }}
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                />
              ))}

              {product.grade && (
                <div className="absolute left-4 top-4 z-10 rounded-full bg-cream-50/90 px-3 py-1.5 text-sm font-semibold text-coffee-200 shadow-soft backdrop-blur-sm">
                  {t('products.grade', 'Grade')} {product.grade}
                </div>
              )}

              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    aria-label={t('products.modal.prevImage', 'Image précédente')}
                    className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60"
                  >
                    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    onClick={nextImage}
                    aria-label={t('products.modal.nextImage', 'Image suivante')}
                    className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60"
                  >
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </button>

                  <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                        aria-label={`${t('products.modal.goToImage', 'Voir image')} ${index + 1}`}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          currentImageIndex === index ? 'w-5 bg-cream-50 shadow-lg' : 'w-2 bg-cream-50/50 hover:bg-cream-50/75'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Details — scrollable content + sticky action bar */}
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="p-6 sm:p-8">
                  <p className="eyebrow mb-2">{categoryLabel}</p>
                  <h2 id="product-modal-title" className="font-display text-2xl font-bold text-coffee-200 sm:text-3xl">
                    {product.name}
                  </h2>
                  <p className="mt-3 leading-relaxed text-coffee-100">
                    {description}
                  </p>

                  {product.certifications && product.certifications.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {product.certifications.map((cert) => (
                        <span key={cert} className="inline-flex items-center gap-1.5 rounded-full bg-forest-200/10 px-3 py-1 text-xs font-semibold text-forest-200">
                          <Check className="h-3.5 w-3.5" aria-hidden="true" />
                          {cert}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Specifications */}
                  <div className="mt-8">
                    <h3 className="mb-4 font-display text-lg font-semibold text-coffee-200">
                      {t('products.modal.specsTitle', 'Spécifications Techniques')}
                    </h3>
                    <dl className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                      {product.specifications.map((spec, index) => (
                        <div key={index} className="flex items-center justify-between gap-3 rounded-xl border border-coffee-200/10 bg-cream-100/60 px-4 py-3">
                          <dt className="shrink-0 text-sm font-medium text-coffee-200">{spec.name}</dt>
                          <dd className="min-w-0 text-right text-sm font-bold text-forest-200">
                            {spec.value}{spec.unit}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>
              </div>

              {/* Sticky action bar */}
              <div className="flex-shrink-0 border-t border-coffee-200/10 bg-cream-50/95 p-5 backdrop-blur-sm sm:p-6">
                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                  <button
                    onClick={onClose}
                    className="text-sm font-semibold text-coffee-100 transition-colors hover:text-coffee-200"
                  >
                    {t('products.modal.close', 'Fermer')}
                  </button>
                  <button
                    onClick={() => { onClose(); onQuoteClick(); }}
                    className="group/btn inline-flex items-center justify-center gap-2 rounded-full bg-forest-200 py-2.5 pl-5 pr-2 text-sm font-semibold text-cream-50 transition-colors hover:bg-forest-100 sm:justify-start"
                  >
                    <span>{t('products.modal.quoteCta', 'Demander un Devis')}</span>
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-cream-50/15 transition-transform duration-300 group-hover/btn:translate-x-0.5">
                      <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default ProductModal;
